import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

/**
 * Тема оформления портала.
 *
 * `system` — не отдельная палитра, а «следовать за ОС»: выбор хранится как есть,
 * а в `data-theme` уходит уже вычисленное `light`/`dark`. Благодаря этому
 * переключение ОС на лету меняет портал, пока пользователь сам не выбрал тему.
 */
export type Theme = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "phygc-portal-theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

const isTheme = (value: unknown): value is Theme =>
  value === "light" || value === "dark" || value === "system";

/** Тема, применённая к документу: `system` уже развёрнут в light/dark. */
export const resolveTheme = (theme: Theme): "light" | "dark" =>
  theme === "system" ? (window.matchMedia(DARK_QUERY).matches ? "dark" : "light") : theme;

const ThemeContext = createContext<{
  theme: Theme;
  resolved: "light" | "dark";
  setTheme: (theme: Theme) => void;
} | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : "system";
  });
  const [resolved, setResolved] = useState<"light" | "dark">(() => resolveTheme(theme));

  const apply = useCallback((next: Theme) => {
    const value = resolveTheme(next);
    document.documentElement.dataset.theme = value;
    // Чтобы нативные контролы (скроллбары, выпадающие списки) не остались светлыми.
    document.documentElement.style.colorScheme = value;
    setResolved(value);
  }, []);

  useEffect(() => {
    apply(theme);
  }, [theme, apply]);

  // В режиме «как в системе» следим за сменой темы ОС без перезагрузки.
  useEffect(() => {
    if (theme !== "system") return;
    const media = window.matchMedia(DARK_QUERY);
    const onChange = () => apply("system");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [theme, apply]);

  const setTheme = useCallback((next: Theme) => {
    localStorage.setItem(THEME_STORAGE_KEY, next);
    setThemeState(next);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme используется вне ThemeProvider");
  return context;
}
